import DisableWindowScroll from 'components/DisableWindowScroll/DisableWindowScroll.component';
import Nothing from 'components/Nothing/Nothing.component';
import invariant from 'invariant';
import React, { KeyboardEvent, SyntheticEvent } from 'react';
import { useSelector, useStore } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import useGetTranslate from 'utils/hooks/useGetTranslate';
import { hideModal, showModal } from './Modal.actions';
import { makeSelectIsModalVisible } from './Modal.selectors';
import { ConnectModalConfig } from './Modal.types';
import ModalHeaderPrimaryVariant from './partials/ModalHeaderPrimaryVariant.component';
import ModalInjector from './partials/ModalInjector.component';
import {
  FocusSentinel,
  ModalContent,
  ModalOverlay,
  ScrollableWrapper,
} from './style/Modal.style';

/**
 * Higher order component that creates a connected modal given the
 * config provided.
 * @param config configuration object.
 * @param config.modalName Unique modal identifier.
 * @param config.showOnMount If true it is initially visible on mount.
 * @param config.headerComponent Component that renders the modal header.
 * @param config.bodyComponent Component that render the modal body.
 * @param config.modifyTheme Options Theme
 * @param config.enableCloseOnEscKey Closes the modal if esc is pressed.
 * @param config.preventCloseOnClickOutside If set to 'true' the modal does not
 * close if the user clicks outside.
 * @example
 * ```typescript
 * const TestModal = withConnectedModal<OptionalBodyComponentType>({
 *  modalName: 'test-modal',
 * showOnMount: true,
 * headerComponent: ModalHeaderSecondaryVariant,
 * bodyComponent: () => (<div />),
 * ```
 *
 * const rendered = <TestModal bodyProp="3" />
 */
export default function withConnectedModal<P = any>({
  modalName,
  showOnMount = true,
  hideOnUnmount = true,
  headerComponent = ModalHeaderPrimaryVariant,
  bodyComponent = Nothing,
  footerComponent = Nothing,
  modifyTheme,
  enableCloseOnEscKey = true,
  preventCloseOnClickOutside = false,
}: ConnectModalConfig<P>) {
  const HeaderComponent = headerComponent;
  const BodyComponent = bodyComponent;
  const FooterComponent = footerComponent;

  invariant(
    typeof modalName === 'string' && modalName.length > 0,
    `connectModal: ${modalName} is not a valid modal identifier`,
  );

  const selectIsModalVisible = makeSelectIsModalVisible(modalName);

  const ConnectedModal = ({
    heading,
    closeBtn,
    i18nHeading,
    i18nHeadingData,
    headingCenter,
    onClose,
    onClickButton,
    hideFooter = true,
    autoMarginBottomManagement = false,
    ...otherProps
  }: any) => {
    const reduxStore = useStore();
    const visible = useSelector(selectIsModalVisible);

    React.useEffect(() => {
      if (showOnMount) {
        // We do not want to have this effect run everytime 'visible' changes
        // nor we can accept 'stale' value hence we intentionally
        // avoid the closure on 'visible'.
        const isVisble = selectIsModalVisible(reduxStore.getState());

        if (!isVisble) {
          reduxStore.dispatch(showModal(modalName));
        }
      }

      return () => {
        if (hideOnUnmount) {
          const isVisble = selectIsModalVisible(reduxStore.getState());

          if (isVisble) {
            reduxStore.dispatch(hideModal(modalName));
          }
        }
      };
    }, [reduxStore]);

    const handleClose = React.useCallback(() => {
      reduxStore.dispatch(hideModal(modalName));

      if (typeof onClose === 'function') {
        onClose();
      }
    }, [onClose, reduxStore]);

    const { handleClickOutside, handleKeyDown } = React.useMemo(
      () => ({
        handleKeyDown: (domEvent: KeyboardEvent<HTMLElement>) => {
          if (enableCloseOnEscKey) {
            if (domEvent.key === 'Escape') {
              handleClose();
            }
          }
        },
        handleClickOutside: (domEvent: SyntheticEvent<HTMLElement>) => {
          if (preventCloseOnClickOutside) {
            return;
          }

          const { target, currentTarget } = domEvent;

          // https://developer.mozilla.org/en-US/docs/Web/API/Event/currentTarget
          if (target === currentTarget) {
            handleClose();
          }
        },
      }),
      [handleClose],
    );
    const translate = useGetTranslate();

    const processedHeading: string | undefined = React.useMemo(() => {
      if (i18nHeading && typeof heading === 'string') {
        return translate(
          heading,
          typeof i18nHeadingData === 'object' && i18nHeadingData !== null
            ? i18nHeadingData
            : null,
        ) as string;
      }

      return heading;
    }, [heading, i18nHeading, i18nHeadingData, translate]);

    if (!visible) {
      return null;
    }

    const visibleModalContent = (
      <ModalOverlay
        onMouseDown={handleClickOutside}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <DisableWindowScroll />
        <ModalContent hideFooter={hideFooter}>
          <FocusSentinel tabIndex={0} data-focus-sentinel="start" />
          <ScrollableWrapper
            autoMarginBottomManagement={autoMarginBottomManagement}
          >
            <HeaderComponent
              headingCenter={headingCenter}
              heading={processedHeading}
              onClose={onClose}
              closeBtn={closeBtn}
            />
            <BodyComponent
              onClose={onClose}
              hideFooter={hideFooter}
              {...otherProps}
            />
            {!hideFooter && (
              <FooterComponent
                onClose={onClose}
                onClickButton={onClickButton ? onClickButton : onClose}
                {...otherProps}
              />
            )}
          </ScrollableWrapper>
          <FocusSentinel tabIndex={0} data-focus-sentinel="end" />
        </ModalContent>
      </ModalOverlay>
    );

    if (modifyTheme) {
      return (
        <ModalInjector modalName={modalName}>
          <ThemeProvider theme={modifyTheme}>
            {visibleModalContent}
          </ThemeProvider>
        </ModalInjector>
      );
    }

    return (
      <ModalInjector modalName={modalName}>{visibleModalContent}</ModalInjector>
    );
  };

  if (!import.meta.env.PROD) {
    ConnectedModal.displayName = `withConnectModal(${
      BodyComponent.displayName || BodyComponent.name || 'Component'
    })`;
  }

  return ConnectedModal;
}
